/*  MUI  */
import {
    ExpandLess,
    ExpandMore,
    UnfoldLess,
    UnfoldMore
} from '@mui/icons-material';
import {
    Box,
    Button,
    Collapse,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';

/*  Shared  */
import { ApplicationNameAdminAppModalCell } from 'application/Shared/Components/ApplicationNameAdminAppModalCell';
import { ManagedTable } from 'application/Shared/Components/Table';
import { Helpers } from 'application/Shared/Helpers';

/*  Objects  */
import { Application } from 'objects/Application';
import { User, UserIconAndName } from 'objects/User';

/*  React  */
import React, { Component, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';

/*  Components  */
import CoverPage from '../Components/Containers/CoverPage';

const UserApps = ({
    renderingScheduledReport,
    reportData,
    reportSetup,
    runReport,
    t
}) => {
    if (reportData === undefined) {
        return null;
    }

    const { userAppsUserAppsOverview } = reportData;
    if (!userAppsUserAppsOverview) {
        return null;
    }
    return (
        <Box width="100%">
            {reportSetup.reportSelections?.userAppsUserAppsCover && (
                <CoverPage reportSetup={reportSetup} />
            )}
            <Box>
                <Overview
                    t={t}
                    reportData={reportData.userAppsUserAppsOverview}
                    renderingScheduledReport={renderingScheduledReport}
                    runReport={runReport}
                />
            </Box>
        </Box>
    );
};

const renderUser = (user) => {
    const { id } = user;

    return (
        <TableRow key={`ilo_bd_${id}`}>
            <TableCell component="th" scope="row">
                <User data={user}>
                    <UserIconAndName includeTitle link />
                </User>
            </TableCell>
        </TableRow>
    );
};

const headersFn = ({ renderingScheduledReport, t }) => {
    const headers = [
        { field: 'expand', renderHeader: '', width: '5%' },
        {
            field: 'application',
            renderHeader: t('Common.application'),
            width: '75%'
        },
        {
            field: 'totalUsers',
            renderHeader: t('Common.total_users'),
            width: '20%'
        }
    ];
    if (renderingScheduledReport) {
        headers.shift();
    }

    return headers;
};

const UserAppsReportTable = ({
    csvData,
    data,
    renderingScheduledReport,
    renderRow
}) => {
    const { t } = useTranslation();

    const csvExportFields = useMemo(
        () => [
            { key: 'appName', label: t('Common.application') },
            { key: 'userName', label: t('Common.user') }
        ],
        [t]
    );

    const headers = useMemo(
        () => headersFn({ renderingScheduledReport, t }),
        [renderingScheduledReport, t]
    );
    return (
        <ManagedTable
            csvData={csvData}
            csvExport
            csvExportFields={csvExportFields}
            csvExportFileName="user_application_inventory"
            headers={headers}
            manageRows
            renderBody={renderRow}
            renderBodyKey="applicationId"
            renderFooter={!renderingScheduledReport}
            rows={data}
            rowsPerPage={-1}
            sortDirection="DESC"
            sortFieldType="number"
            sortKey="totalUsers"
        />
    );
};

class Overview extends Component {
    constructor(props) {
        super(props);
        this.state = {
            csvData: [],
            data: [],
            loaded: false,
            showCollapse: false,
            showExpand: true,
            total: 0
        };
    }

    componentDidMount() {
        const { reportData } = this.props;
        if (Helpers.isEmpty(reportData)) {
            return;
        }

        const { data } = reportData;
        if (Array.isArray(data)) {
            this.load(data);
        }
    }

    load = (reportData) => {
        const { t } = this.props;

        const csvData = [];
        const data = reportData.map((d) => {
            const { application, users } = d;
            const appName = application.name;

            if (users.length === 0) {
                csvData.push({ appName, userName: t('Common.no_users') });
            }

            users.forEach((user) => {
                csvData.push({ appName, userName: user.name });
            });

            return { ...d, expanded: false };
        });

        this.setState({ csvData, data, loaded: true, total: data.length });
    };

    handleAll = (expanded) => {
        let { data } = this.state;
        data = data.map((d) => ({ ...d, expanded }));
        this.setState({ data, showCollapse: expanded, showExpand: !expanded });
    };

    expand = (row) => {
        const { total } = this.state;
        let { data } = this.state;

        let expandedCount = 0;
        let collapsedCount = 0;

        data = data.map((d) => {
            const { application } = d;
            let { expanded } = d;
            if (application.id !== row.application.id) {
                if (expanded) {
                    expandedCount += 1;
                } else {
                    collapsedCount += 1;
                }
                return d;
            }

            expanded = !expanded;
            if (expanded) {
                expandedCount += 1;
            } else {
                collapsedCount += 1;
            }

            return { ...d, expanded };
        });

        let showExpand = false;
        let showCollapse = false;
        if (expandedCount > 0) {
            if (expandedCount !== total) {
                showCollapse = true;
            }
        } else {
            showExpand = true;
        }

        if (collapsedCount > 0) {
            if (collapsedCount !== total) {
                showExpand = true;
            }
        } else {
            showCollapse = true;
        }

        this.setState({ data, showCollapse, showExpand });
    };

    renderButtons = () => {
        const { t } = this.props;
        const { showExpand, showCollapse } = this.state;

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginBottom: '8px',
                    marginTop: '8px',
                    width: '100%'
                }}
            >
                {showCollapse && (
                    <Button
                        variant="outlined"
                        className="no-print"
                        color="primary"
                        style={{ marginRight: showExpand ? '8px' : 0 }}
                        onClick={() => this.handleAll(false)}
                    >
                        <UnfoldLess
                            style={{ marginRight: '8px' }}
                            fontSize="small"
                        />
                        {t('Reports.collapse_all')}
                    </Button>
                )}
                {showExpand && (
                    <Button
                        variant="contained"
                        className="no-print"
                        color="primary"
                        onClick={() => this.handleAll(true)}
                    >
                        <UnfoldMore
                            style={{ marginRight: '8px' }}
                            fontSize="small"
                        />
                        {t('Reports.expand_all')}
                    </Button>
                )}
            </Box>
        );
    };

    renderAppRow = (row) => {
        const { renderingScheduledReport, runReport } = this.props;
        const { application, expanded, totalUsers } = row;

        return (
            <Application data={application}>
                {!renderingScheduledReport && (
                    <TableCell>
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={() => {
                                this.expand(row);
                            }}
                            sx={{ color: 'icon.textColor' }}
                        >
                            {expanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                    </TableCell>
                )}
                <TableCell>
                    <ApplicationNameAdminAppModalCell
                        onCloseModal={runReport}
                        row={row}
                        nameNoTruncate
                    />
                </TableCell>
                <TableCell align="left">{totalUsers}</TableCell>
            </Application>
        );
    };

    renderUsers = (row) => {
        const { t } = this.props;
        const { users } = row;

        if (users.length === 0) {
            return (
                <TableBody>
                    <TableRow>
                        <TableCell>
                            <Typography variant="h5">
                                {t('Common.no_users')}
                            </Typography>
                        </TableCell>
                    </TableRow>
                </TableBody>
            );
        }

        return <TableBody>{users.map((user) => renderUser(user))}</TableBody>;
    };

    renderRow = (row) => {
        const { application, expanded } = row;

        return (
            <React.Fragment key={`ilo_${application.id}`}>
                <TableRow sx={{ backgroundColor: 'table.rowBackground' }}>
                    {this.renderAppRow(row)}
                </TableRow>
                <TableRow
                    variant="noHover"
                    sx={{ backgroundColor: 'table.subtableBackground' }}
                >
                    <TableCell
                        sx={{
                            paddingBottom: 0,
                            paddingTop: 0
                        }}
                        colSpan={6}
                    >
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                            <Paper
                                elevation={0}
                                sx={{
                                    backgroundColor: 'table.subtableBackground'
                                }}
                            >
                                <Box margin="4px" padding="8px">
                                    <Table size="small">
                                        {this.renderUsers(row)}
                                    </Table>
                                </Box>
                            </Paper>
                        </Collapse>
                    </TableCell>
                </TableRow>
            </React.Fragment>
        );
    };

    render() {
        const { t, renderingScheduledReport } = this.props;
        const { csvData, data, loaded } = this.state;

        if (!loaded) {
            return null;
        }

        return (
            <Box m="32px" data-trackid="reportsUserAppsRenderMainWrapper">
                <Box data-trackid="reportsUserAppsRenderHeaderWrapper">
                    <Typography variant="h1" style={{ fontWeight: '500' }}>
                        {t('Reports.Title.userApps')}
                    </Typography>
                    <Typography paragraph>
                        {t('Reports.UserApps.description')}
                    </Typography>
                </Box>
                {this.renderButtons()}
                <UserAppsReportTable
                    csvData={csvData}
                    data={data}
                    renderingScheduledReport={renderingScheduledReport}
                    renderRow={this.renderRow}
                />
            </Box>
        );
    }
}

export default withTranslation()(UserApps);
